import { getThread, setToastMessage } from "../stores";
import { ChatChannelInstance, ChatLocalMesage, ChatServiceCallbacks, ForwardMessagesProps, HandleReactionProps, RemoveReactionProps, SendQuickReplyProps, TogglePinMessageProps } from "../types";
import { BaseChatPackage, canSendQuickReply, getReactionType, resolveRemindAtISO, isReminderTooSoon, toFriendlyReminderError } from "../utils";
import { Constants } from "../constants";
import { alert } from '@codeleap/modals'
import { findExcludedWordInText } from './excludedWords'
import { EXCLUDED_WORDS_QUERY_KEY } from '../hooks/useExcludedWords'
import { withRateLimit, withRetry } from "../utils/rateLimiter";
import { Attachment } from "stream-chat";
import { waitFor } from '@codeleap/utils'

export class MessagesServices extends BaseChatPackage {
  async forwardMessages({ channelsToForward, messages, messageText, onProgress, onFinally, onOpenChannel }: ForwardMessagesProps) {
    try {
      const chatClient = await this.getChatClient()
      const normalizedMessageText = messageText?.trim() || ''

      const getCreatedAtTime = (message: {
        created_at?: string | Date | null
        createdAt?: string | Date | null
      }) => {
        const createdAt = message?.created_at ?? message?.createdAt
        if (!createdAt) return null
        const timestamp = new Date(createdAt).getTime()
        return Number.isFinite(timestamp) ? timestamp : null
      }

      const messagesToForward = messages
        .map((message, originalIndex) => ({
          message,
          originalIndex,
          createdAtTime: getCreatedAtTime(message),
        }))
        .filter(({ message }) => !message?.poll_id)
        .sort((a, b) => {
          if (a.createdAtTime != null && b.createdAtTime != null) {
            if (a.createdAtTime !== b.createdAtTime) {
              return a.createdAtTime - b.createdAtTime
            }
            return a.originalIndex - b.originalIndex
          }

          if (a.createdAtTime != null) return -1
          if (b.createdAtTime != null) return 1

          return a.originalIndex - b.originalIndex
        })
        .map(({ message }) => message)

      const excludedWords = this.queryClient.client.getQueryData<string[]>(EXCLUDED_WORDS_QUERY_KEY) ?? []
      const blockedMessage = messagesToForward.find(m => m.text && findExcludedWordInText(excludedWords, m.text))
      const blockedComment = normalizedMessageText ? findExcludedWordInText(excludedWords, normalizedMessageText) : null

      if (blockedMessage || blockedComment) {
        alert.warn({
          title: 'Blocked message',
          body: 'You are trying to send a word that is not allowed. Please review your message and try again.',
          options: [{ text: 'OK', style: 'default' }],
        })
        return
      }

      const forwardPayloads = messagesToForward.map(({ attachments, text }) => ({
        text,
        attachments,
        // @ts-ignore
        custom: { forwarded: true },
      }))

      const totalSends = channelsToForward.length * (messagesToForward.length + (normalizedMessageText ? 1 : 0))
      let completedSends = 0

      if (channelsToForward.length === 1) {
        await waitFor(50) // Wait for the channel to be fully ready before sending messages
        onOpenChannel?.(channelsToForward[0])
      }

      const results = []

      for (const simpleChannel of channelsToForward) {
        const targetChannel = chatClient.channel(simpleChannel.type, simpleChannel.id)

        try {
          for (const payload of forwardPayloads) {
            await withRetry(async () => {
              await withRateLimit(async () => {
                await this.chatApi.sendMessage(targetChannel, payload)
              })
            })

            completedSends++
            onProgress?.(completedSends, totalSends)
          }

          if (normalizedMessageText) {
            await withRetry(async () => {
              await withRateLimit(async () => {
                await this.chatApi.sendMessage(targetChannel, {
                  text: normalizedMessageText,
                })
              })
            })

            completedSends++
            onProgress?.(completedSends, totalSends)
          }

          results.push({ channel: simpleChannel, success: true })
        } catch (error) {
          console.error(`Error forwarding messages to channel ${simpleChannel.id}:`, error)
          results.push({
            channel: simpleChannel,
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error'
          })
        }
      }

      return results
    } catch(e) {
      console.error('Error forwarding messages', e)
      throw e
    } finally {
      onFinally?.()
    }
  }
  
  // @spec FR-0041@v2 — set message reminder.
  // @spec BR-0022@v1 — same-minute conflict guard.
  async createReminder({ messageId, date, time, onFinally, onError }: {
    messageId: ChatLocalMesage['id'],
    date: Date, time: Date
  } & ChatServiceCallbacks) {
    try {
      const chatClient = await this.getChatClient()
      const chatUser = chatClient.user

      // Minute-aligned so the same-minute duplicate query below can reuse it.
      const remindAt = resolveRemindAtISO(date, time)

      // Guard before any GetStream round-trip below.
      if (isReminderTooSoon(remindAt)) {
        alert.error({
          title: 'Invalid reminder time',
          body: Constants.reminder.tooSoonMessage,
        })
        return
      }

      const remindersResponse = await chatClient.queryReminders({ filter: { message_id: messageId } })

      const createReminder = async () => {
        const remindAtMinuteEnd = new Date(new Date(remindAt).getTime() + 60000).toISOString()

        const sameMinuteRemindersResponse = await chatClient.queryReminders({
          filter: {
            $and: [
              { remind_at: { $gte: remindAt } },
              { remind_at: { $lt: remindAtMinuteEnd } }
            ]
          }
        })

        // If there are more than 2 reminders at the same minute, show alert
        if (sameMinuteRemindersResponse?.reminders?.length >= 2) {
          onFinally?.()
          alert.error({
            title: 'Too many reminders',
            body: 'You already have 2 or more reminders set at this minute. Please choose a different time.',
          })
          return
        }

        const devices = (await chatClient.getDevices())?.devices

        const response = await chatClient.createReminder({
          messageId,
          remind_at: remindAt
        })

        setToastMessage(`Your message is set`)

        // For some reason, Getstream is unregistering devices when creating reminders. Re-add them
        for (const device of devices) {
          if (device.push_provider === 'firebase') {
            await chatClient.addDevice(device.id, device.push_provider, chatUser.id, device.push_provider_name)
          }
        }

        return response
      }

      if (remindersResponse?.reminders?.length) {
        const lastReminder = remindersResponse?.reminders?.[0]
        const now = new Date().toISOString()

        // If has an reminder that is in the future, ask to update
        if (lastReminder?.remind_at > now) {
          alert.ask({
            title: `Reminder already set`,
            body: `You already have a reminder set for this message. Do you want to update it?`,
            options: [
              { text: `Cancel`, style: 'cancel', onPress: onFinally },
              { text: `Confirm` , onPress: async () => {
                chatClient.updateReminder({ messageId, remind_at: remindAt }).then(() => {
                  setToastMessage(`Your reminder is updated`)
                })
              }}
            ]
          })
        } else {
          await chatClient.deleteReminder(messageId)
          return createReminder()
        }
        return
      }


      return createReminder()
    } catch (e) {
      console.error('Error creating reminder', e)
      const friendlyError = toFriendlyReminderError(e)
      onError?.(friendlyError)
      throw friendlyError
    } finally {
      onFinally?.()
    }
  }

  async togglePinMessage({ channel, message }: TogglePinMessageProps) {
    try {
      const chatClient = await this.getChatClient()

      if (message?.pinned) {
        const result = await chatClient?.unpinMessage(message)
        return { unpinned: true, messageId: message.id }
      }

      const channelInstance = chatClient.channel(channel?.type, channel.id)

      const channelState = await channelInstance.query()
      const pinnedMessages = channelState?.pinned_messages || []

      await chatClient?.pinMessage(message)

      let promises = []

      pinnedMessages.forEach(pm => promises.push(chatClient.unpinMessage(pm)))

      await Promise.all(promises)

      this.chatApi.sendMessageSystem({ text: 'pinned a message', channel }).catch(error => {
        console.warn('[ChatServices] Failed to send pin system message:', error)
      })

      return { pinned: true, messageId: message.id }
    } catch (error) {
      console.error('[ChatServices] Error toggling pin:', error)
      return null
    }
  }
  
  async sendGiphy({ channel, title, aspectRatio, id, quotedMessageId }: { channel: ChatChannelInstance, title?: string, aspectRatio: number, id: string, quotedMessageId?: ChatLocalMesage['id'] }) {
    const thread = getThread()

    return this.chatApi.sendMessage(
      channel, {
      parent_id: thread?.id || null,
      quoted_message_id: quotedMessageId,
      attachments: [
        {
          type: 'giphy',
          title: title || 'GIF',
          mimetype: 'image/gif',
          custom: {
            giphy_id: id,
            aspect_ratio: aspectRatio,
          }
        } as any,
      ],
    })
  }

  async updateMessage(
    message: ChatLocalMesage,
    text: string,
    mentionedUsers?: Array<{ id: string; name: string; image?: string }>
  ) {
    try {
      const chatClient = await this.getChatClient()

      // Stream's TS type declares `mentioned_users: UserResponse[]` here even
      // though the REST endpoint only consumes the `id` field of each user
      // (see stream-chat client.d.ts: `updateMessage` payload). Build minimal
      // `{ id }` rows instead of casting — keeps the call type-correct while
      // matching the wire shape the backend actually parses.
      const mentioned_users = mentionedUsers?.map(user => ({ id: user.id })) || []

      const response = await chatClient.updateMessage({
        ...message,
        text,
        mentioned_users,
      })
      return response.message
    } catch (e) {
      console.warn('Error updating message', e)
      throw e
    }
  }
  
  async handleReaction({message, channel, onReaction, onReactionSent, emoji }: HandleReactionProps) {
    try {
      const myReactions = message?.own_reactions || []

      onReaction?.(emoji)
      const existentReaction = myReactions?.find(reaction => reaction.emoji_code === emoji)

      if (existentReaction) {
        return channel.deleteReaction(message?.id, existentReaction?.type)
      }

      const reactionsToRemove = myReactions.filter(reaction => reaction.emoji_code !== emoji && !!reaction?.type)

      if (reactionsToRemove.length) {
        await Promise.all(
          reactionsToRemove.map(reaction => channel.deleteReaction(message?.id, reaction?.type)),
        )
      }

      const reactionType = getReactionType(emoji)

      const reaction = await channel.sendReaction(message?.id, {
        type: reactionType,
        emoji_code: emoji,
      }, { enforce_unique: true })

      onReactionSent?.(reaction)
      return reaction
    } catch (error) {
      console.error('Error sending reaction', error)
      throw error
    }
  }

  /** Deletes `reaction` from `message` via the Stream channel and fires `onReactionRemoved` on success. */
  async removeReaction({ message, channel, reaction, onReactionRemoved }: RemoveReactionProps) {
    const type = reaction?.type
    if (!type) return null
    try {
      const result = await channel.deleteReaction(message?.id, type)
      onReactionRemoved?.(reaction)
      return result
    } catch (error) {
      console.error('Error removing reaction', error)
      throw error
    }
  }

  async deleteMessages(messages: ChatLocalMesage['id'][]) {
    try {
      const chatClient = await this.getChatClient()
      const deletePromises = messages.map(msgId => chatClient.deleteMessage(msgId))
      await Promise.all(deletePromises)
    } catch (e) {
      console.error('Error deleting messages', e)  
      throw e
    }
  }

  sendAudio(
    channel: ChatChannelInstance,
    audioAttachment: Attachment,
    opts?: { quotedMessageId?: ChatLocalMesage['id'] },
  ) {
    const parentMessageId = getThread()?.id

    return this.chatApi.sendMessage(channel, {
      attachments: [audioAttachment],
      parent_id: parentMessageId,
      quoted_message_id: opts?.quotedMessageId,
    })
  }

  // @spec FR-0077@v1 — Quick Reply.
  // @spec FR-0077/AC-8@v1 — thread vs channel/DM routing.
  // @spec BR-0030@v1 — empty-send guard.
  async sendQuickReply({ channel, draft, onSuccess, onError, onFinally }: SendQuickReplyProps) {
    try {
      if (!canSendQuickReply(draft)) {
        throw new Error(
          `sendQuickReply called with empty draft (text='${draft?.text ?? ''}', attachments=${draft?.attachments?.length ?? 0}) — BR-0030 requires at least text or one attachment.`,
        )
      }

      const parentMessageId = draft.parentId ?? getThread()?.id ?? null
      const attachments = draft.attachments?.length ? draft.attachments : undefined

      const response = await this.chatApi.sendMessage(channel, {
        text: draft.text?.trim() || '',
        attachments,
        quoted_message_id: draft.quotedMessageId,
        parent_id: parentMessageId,
      })

      onSuccess?.(response)
      return response
    } catch (e) {
      console.error('Error sending quick reply', e)
      onError?.(e as Error)
      throw e
    } finally {
      onFinally?.()
    }
  }
}
